<template>
    <div class="comment-modal-overlay" v-if="isVisible" @click="closeModal">
        <div class="comment-modal" @click.stop>
            <div class="modal-header">
                <h3 class="modal-title">
                    <span class="title-icon">💬</span>
                    댓글 관리
                </h3>
                <button class="close-button" @click="closeModal">
                    <span class="close-icon">×</span>
                </button>
            </div>

            <div class="modal-content">
                <div class="comment-section">
                    <h4 class="section-title">
                        기존 댓글 ({{ commentList.length }}개)
                    </h4>
                    
                    <div class="comment-list">
                        <div 
                            v-for="(comment, index) in commentList" 
                            :key="index"
                            class="comment-item"
                            :class="{ 'comment-error': comment.hasError }"
                        >
                            <div class="comment-number">
                                {{ comment.index }}
                            </div>
                            <div class="comment-content">
                                <div 
                                    class="comment-text"
                                    :class="{ 'comment-text-error': comment.hasError }"
                                    @click="editComment(index, comment.content)"
                                    :title="comment.hasError ? '' : '클릭하여 수정'"
                                >
                                    {{ comment.content }}
                                </div>
                                <div v-if="comment.date" class="comment-date">
                                    📅 {{ comment.date }}
                                </div>
                                <div class="comment-actions">
                                    <button 
                                        class="action-button edit-button"
                                        @click="editComment(index, comment.content)"
                                        :disabled="comment.hasError"
                                    >
                                        ✏️ 수정
                                    </button>
                                    <button 
                                        class="action-button delete-button"
                                        @click="deleteComment(index)"
                                        :disabled="comment.hasError"
                                    >
                                        🗑️ 삭제
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-actions">
                    <button class="modal-button add-button" @click="addNewComment">
                        ➕ 댓글 추가
                    </button>
                    <button class="modal-button cancel-button" @click="closeModal">
                        ❌ 닫기
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PaymentCommentModal',
    
    props: {
        isVisible: {
            type: Boolean,
            default: false
        },
        transaction: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            commentList: [],
            editingIndex: -1,
            editingContent: ''
        };
    },

    watch: {
        isVisible(newVal) {
            if (newVal && this.transaction) {
                this.prepareCommentList();
            }
        }
    },

    methods: {
        prepareCommentList() {
            if (!this.transaction?.comments || !Array.isArray(this.transaction.comments)) {
                this.commentList = [];
                return;
            }

            this.commentList = this.transaction.comments.map((comment, index) => {
                if (!comment) {
                    return { 
                        index: index + 1, 
                        content: '(댓글 데이터 오류)', 
                        date: '', 
                        hasError: true 
                    };
                }

                if (!comment.comment && comment.comment !== '') {
                    const possibleContent = comment.text || comment.message || comment.content || comment.body || '';
                    if (possibleContent) {
                        return {
                            index: index + 1,
                            content: possibleContent,
                            date: this.formatCommentDate(comment.created_at || comment.createdAt || comment.date),
                            hasError: false
                        };
                    }
                    return { 
                        index: index + 1, 
                        content: '(내용 없음 - comment 필드 누락)', 
                        date: '', 
                        hasError: true 
                    };
                }

                return {
                    index: index + 1,
                    content: comment.comment,
                    date: this.formatCommentDate(comment.created_at || comment.createdAt || comment.date),
                    hasError: false
                };
            });
        },

        editComment(index, currentContent) {
            this.editingIndex = index;
            this.editingContent = currentContent;
            
            // 수정 모드로 전환
            this.$nextTick(() => {
                const commentItem = this.$el.querySelector(`.comment-item:nth-child(${index + 1})`);
                if (commentItem) {
                    const contentDiv = commentItem.querySelector('.comment-content');
                    contentDiv.innerHTML = `
                        <textarea class="edit-textarea" 
                                  maxlength="500" 
                                  rows="4"
                                  placeholder="댓글을 수정하세요...">${currentContent}</textarea>
                        <div class="edit-actions">
                            <button class="action-button save-button" onclick="this.closest('.comment-item').__vue__.saveComment(${index})">
                                💾 저장
                            </button>
                            <button class="action-button cancel-edit-button" onclick="this.closest('.comment-item').__vue__.cancelEdit(${index})">
                                ❌ 취소
                            </button>
                        </div>
                    `;
                }
            });
        },

        saveComment(index) {
            const textarea = this.$el.querySelector(`.comment-item:nth-child(${index + 1}) .edit-textarea`);
            if (!textarea) return;

            const newContent = textarea.value.trim();
            if (newContent === '') {
                alert('댓글 내용을 입력해주세요.');
                return;
            }

            // 부모 컴포넌트에 저장 요청
            this.$emit('save-comment', { index, content: newContent });
            
            // UI 업데이트
            this.commentList[index].content = newContent;
            this.editingIndex = -1;
            this.editingContent = '';
            
            // 수정 완료 표시
            this.showEditSuccess(index);
        },

        cancelEdit(index) {
            this.editingIndex = -1;
            this.editingContent = '';
            this.prepareCommentList();
        },

        deleteComment(index) {
            if (confirm('정말로 이 댓글을 삭제하시겠습니까?')) {
                this.$emit('delete-comment', { index });
                this.commentList.splice(index, 1);
            }
        },

        addNewComment() {
            this.$emit('add-comment');
            this.closeModal();
        },

        closeModal() {
            this.$emit('close');
        },

        showEditSuccess(index) {
            const commentItem = this.$el.querySelector(`.comment-item:nth-child(${index + 1})`);
            if (commentItem) {
                commentItem.classList.add('edit-success');
                setTimeout(() => {
                    commentItem.classList.remove('edit-success');
                }, 2000);
            }
        },

        formatCommentDate(dateString) {
            if (!dateString) return '';

            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return dateString;

                const now = new Date();
                const diffTime = Math.abs(now - date);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays === 1) return '오늘';
                if (diffDays === 2) return '어제';
                if (diffDays <= 7) return `${diffDays - 1}일 전`;
                
                return date.toLocaleDateString('ko-KR');
            } catch (error) {
                return dateString;
            }
        }
    }
};
</script>

<style scoped>
/* 모달 오버레이 */
.comment-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
}

/* 모달 컨테이너 */
.comment-modal {
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow: hidden;
    animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* 모달 헤더 */
.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 24px 16px;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.modal-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
}

.title-icon {
    font-size: 24px;
}

.close-button {
    background: none;
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;
}

.close-button:hover {
    background: rgba(255, 255, 255, 0.2);
}

.close-icon {
    line-height: 1;
}

/* 모달 콘텐츠 */
.modal-content {
    padding: 24px;
    max-height: 60vh;
    overflow-y: auto;
}

.comment-section {
    margin-bottom: 24px;
}

.section-title {
    margin: 0 0 16px 0;
    color: #1f2937;
    font-weight: 600;
    font-size: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e5e7eb;
}

/* 댓글 목록 */
.comment-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.comment-item {
    padding: 16px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    display: flex;
    gap: 12px;
}

.comment-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.comment-item.edit-success {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.comment-item.comment-error {
    background: #fef2f2;
    border-color: #fecaca;
}

/* 댓글 번호 */
.comment-number {
    width: 32px;
    height: 32px;
    background: #3b82f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
}

.comment-error .comment-number {
    background: #ef4444;
}

/* 댓글 콘텐츠 */
.comment-content {
    flex: 1;
    min-width: 0;
}

.comment-text {
    color: #374151;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 8px;
    word-break: break-word;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: background-color 0.2s ease;
}

.comment-text:hover {
    background: #f3f4f6;
}

.comment-text-error {
    color: #dc2626;
    cursor: default;
}

.comment-text-error:hover {
    background: transparent;
}

.comment-date {
    color: #9ca3af;
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 8px;
}

/* 댓글 액션 버튼 */
.comment-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    padding: 6px 12px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
}

.action-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.edit-button {
    background: #3b82f6;
    color: white;
}

.edit-button:hover:not(:disabled) {
    background: #2563eb;
}

.delete-button {
    background: #ef4444;
    color: white;
}

.delete-button:hover:not(:disabled) {
    background: #dc2626;
}

.save-button {
    background: #10b981;
    color: white;
}

.save-button:hover:not(:disabled) {
    background: #059669;
}

.cancel-edit-button {
    background: #6b7280;
    color: white;
}

.cancel-edit-button:hover:not(:disabled) {
    background: #4b5563;
}

/* 수정 모드 스타일 */
.edit-textarea {
    width: 100%;
    min-height: 80px;
    padding: 8px;
    border: 2px solid #3b82f6;
    border-radius: 6px;
    font-size: 14px;
    resize: vertical;
    font-family: inherit;
    margin-bottom: 8px;
}

.edit-actions {
    display: flex;
    gap: 8px;
}

/* 모달 액션 버튼 */
.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
}

.modal-button {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.add-button {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
}

.add-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.cancel-button {
    background: #6b7280;
    color: white;
}

.cancel-button:hover {
    background: #4b5563;
}

/* 반응형 디자인 */
@media (max-width: 640px) {
    .comment-modal {
        width: 95%;
        margin: 20px;
    }
    
    .modal-header {
        padding: 20px 20px 12px;
    }
    
    .modal-content {
        padding: 20px;
    }
    
    .comment-item {
        padding: 12px;
    }
    
    .comment-actions {
        flex-direction: column;
    }
    
    .modal-actions {
        flex-direction: column;
    }
}
</style>